import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FilterDrawer from '../../shared/FilterDrawer';
import ReportOverlay from '../../shared/ReportOverlay';
import BoxLabelView from './BoxLabelView';
import SenderLabelsView from './SenderLabelsView';
import CancelButton from '../../shared/CancelButton';
import SaveButton from '../../shared/SaveButton';
import RequiredMark from '../../shared/RequiredMark';
import DateInput from '../../shared/DateInput';
import { formatNumber } from '../../shared/numberFormat';
import { useShipmentWeightUnit } from '../../shared/useShipmentWeightUnit';
import { ShipDateWarning } from './ShipConfirmFields';
import ShipmentCarriageFields, { carriageFromShipment, carriagePayload } from './ShipmentCarriageFields';
import ShipmentPackages from './ShipmentPackages';
import PackageDetailsFields from './PackageDetailsFields';
import ActualPostageField from './ActualPostageField';
import { detailsDraft, detailsPayload, useDefaultTaxRate, incGstString, shipmentShippingCharge } from './packageShared';
import '../../shared/StyledInput.css';
import { showMessage } from '../../shared/AppDialogs';

// Same field-wrap look as LogActivityDrawer.js's own FilterDrawer body — a
// multi-field edit form belongs in a drawer (ui-architecture.md's "Inline
// Row Expand vs. a Drawer").
const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };

// Edit Shipment — one shipment's Date Shipped, Carrier, Service, Options,
// tracking number and each package's packaging, dimensions and weight, with
// what's in the packages and its items shown read-only. Shared by
// the Shipments list and Pack / Ship's Shipped section. Which order a
// shipment belongs to is set once, when it ships, so it's shown as plain
// context. onSaved gets the updated shipment. canPrintLabels offers a
// package's Package Label and the shipment's Sender Labels, at a packing
// station; they open inside the drawer so it stays open, edits and all.
// Each outermost package also takes its Actual Postage, inc GST (issue
// #350). Once invoiced its Date Shipped is fixed, because the Invoice
// carries it (issue #269).
function ShipmentEditDrawer({ shipment, onClose, onSaved, canPrintLabels }) {
  const navigate = useNavigate();
  const [labelPackageId, setLabelPackageId] = useState(null);
  const [senderLabelsOpen, setSenderLabelsOpen] = useState(false);
  const weightUnit = useShipmentWeightUnit();
  const taxRate = useDefaultTaxRate();
  const [draft, setDraft] = useState(() => ({
    tracking_number: shipment.tracking_number || '',
    shipped_date: shipment.shipped_date || '',
    ...carriageFromShipment(shipment),
  }));
  // Package details being edited, by package id — saved with the shipment.
  const [packageDrafts, setPackageDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const invoiced = !!shipment.invoice && !shipment.invoice.voided_at;

  const outerIds = new Set((shipment.packages || []).map((p) => String(p.id)));
  const postageDraft = (pkg) => ({ actual_postage_inc_gst: incGstString(pkg.actual_postage, taxRate) });
  const openDetails = (pkg) => setPackageDrafts((prev) => (prev[pkg.id] ? prev : {
    ...prev,
    [pkg.id]: { ...detailsDraft(pkg), ...(outerIds.has(String(pkg.id)) ? postageDraft(pkg) : {}) },
  }));
  const setPackageDraft = (pkg, next) => setPackageDrafts((prev) => ({ ...prev, [pkg.id]: { ...prev[pkg.id], ...next } }));
  const packageEditor = (pkg) => (packageDrafts[pkg.id] ? (
    <div style={{ marginTop: 8, padding: 10, background: '#fff', border: '1px solid #e3e7ee', borderRadius: 6 }}>
      <PackageDetailsFields value={packageDrafts[pkg.id]} onChange={(next) => setPackageDraft(pkg, next)} />
      {'actual_postage_inc_gst' in packageDrafts[pkg.id] && (
        <ActualPostageField
          value={packageDrafts[pkg.id].actual_postage_inc_gst}
          onChange={(next) => setPackageDraft(pkg, { actual_postage_inc_gst: next })}
        />
      )}
    </div>
  ) : null);

  // Actual postage is sent only when it was changed, so saving the
  // package's other details leaves it exactly as it was.
  const packagePayload = (id, d) => {
    const pkg = (shipment.packages || []).find((p) => String(p.id) === String(id));
    const body = { id: Number(id), ...detailsPayload(d) };
    if ('actual_postage_inc_gst' in d && pkg && d.actual_postage_inc_gst !== postageDraft(pkg).actual_postage_inc_gst) {
      body.actual_postage_inc_gst = d.actual_postage_inc_gst === '' ? null : d.actual_postage_inc_gst;
    }
    return body;
  };

  const save = async () => {
    if (!draft.shipped_date) return;
    setSaving(true);
    try {
      const res = await axios.patch(`/api/shipments/${shipment.id}`, {
        tracking_number: draft.tracking_number.trim(),
        shipped_date: draft.shipped_date,
        ...carriagePayload(draft),
        packages: Object.entries(packageDrafts).map(([id, d]) => packagePayload(id, d)),
      });
      onSaved(res.data.data);
    } catch (err) {
      await showMessage(err.response?.data?.message || 'Failed to save shipment', { title: 'Couldn’t Save Shipment' });
    }
    setSaving(false);
  };

  return (
    <FilterDrawer
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      onEscapeKeyDown={(e) => { if (labelPackageId || senderLabelsOpen) e.preventDefault(); }}
      title={shipment.display_number ? `Edit Shipment ${shipment.display_number}` : 'Edit Shipment'}
      description={`Update this shipment on ${shipment.order?.type_label || 'Order'} ${shipment.order?.display_number || ''}.`}
      footer={(
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <CancelButton onClick={onClose} disabled={saving} />
          <SaveButton onClick={save} loading={saving} disabled={!draft.shipped_date} type="button" />
        </div>
      )}
    >
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Order</label>
        <div style={{ fontSize: 14 }}>{shipment.order?.type_label} {shipment.order?.display_number} — {shipment.order?.party?.name}</div>
      </div>
      {shipment.invoice && (
        <div style={fieldWrapStyle}>
          <label style={fieldLabelStyle}>Invoice</label>
          <div style={{ fontSize: 14 }}>
            <button
              type="button"
              onClick={() => navigate(`/receivables-invoices/${shipment.invoice.id}`)}
              title="Open the Invoice"
              style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 14 }}
            >
              {shipment.invoice.display_number}
            </button>
            {shipment.invoice.voided_at ? ' (Void)' : ''}
          </div>
        </div>
      )}
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Date Shipped<RequiredMark /></label>
        <DateInput
          className="styled-input"
          value={draft.shipped_date}
          onChange={(e) => setDraft((prev) => ({ ...prev, shipped_date: e.target.value }))}
          disabled={invoiced}
          style={{ width: '100%', fontSize: 14 }}
        />
        {invoiced && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Fixed by Invoice {shipment.invoice.display_number}.</div>}
        <ShipDateWarning date={draft.shipped_date} orderDate={shipment.order?.order_date} />
      </div>
      {/* This shipment only — never changes the order's own
          Carrier/Service/Options or what it charges. */}
      <ShipmentCarriageFields
        value={{ carrier_id: draft.carrier_id, shipping_service_id: draft.shipping_service_id, shipping_option_ids: draft.shipping_option_ids }}
        onChange={(carriage) => setDraft((prev) => ({ ...prev, ...carriage }))}
      />
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Tracking #</label>
        <input
          className="styled-input"
          value={draft.tracking_number}
          onChange={(e) => setDraft((prev) => ({ ...prev, tracking_number: e.target.value }))}
          style={{ width: '100%', fontSize: 14 }}
        />
      </div>
      {/* What's in a shipped package never changes (issue #299) — Reverse
          the shipment for that. Its packaging, dimensions and weight can be
          corrected here. */}
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Packages</label>
        <ShipmentPackages
          packages={shipment.packages}
          weightUnit={weightUnit}
          onLabel={canPrintLabels ? setLabelPackageId : null}
          onSenderLabels={canPrintLabels ? () => setSenderLabelsOpen(true) : null}
          onDetails={openDetails}
          editor={packageEditor}
          shippingCharge={shipmentShippingCharge(shipment)}
        />
      </div>
      {/* Read-only — which SKUs/qty this shipment covered, from
          shipment_lines (written by the Ship pass that created it).
          Shipments from before that table existed have none recorded. */}
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Items</label>
        {shipment.lines && shipment.lines.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {shipment.lines.map((l) => (
              <div key={l.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 13, padding: '6px 10px', borderRadius: 6, background: '#f6f8fa' }}>
                <span style={{ fontWeight: 600, minWidth: 90 }}>{l.sku}</span>
                <span style={{ color: '#666', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</span>
                <span style={{ color: '#333', whiteSpace: 'nowrap' }}>Qty {formatNumber(l.qty, { maxDecimals: 4 })}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: '#888' }}>No item detail recorded for this shipment</div>
        )}
      </div>
      {labelPackageId && (
        <ReportOverlay onClose={() => setLabelPackageId(null)}>
          <BoxLabelView packageId={labelPackageId} onClose={() => setLabelPackageId(null)} />
        </ReportOverlay>
      )}
      {senderLabelsOpen && (
        <ReportOverlay onClose={() => setSenderLabelsOpen(false)}>
          <SenderLabelsView shipmentId={shipment.id} onClose={() => setSenderLabelsOpen(false)} />
        </ReportOverlay>
      )}
    </FilterDrawer>
  );
}

export default ShipmentEditDrawer;
